import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getGroupById, getMondlyUser, isPrivateGroup, getHashWizard } from '../helpers/Network';
import { Children, User } from '../helpers/types';
import { getFromLocalStorage } from '../helpers/utils';
import { useMixpanelContext } from './MixpanelContext';
import {
  AuthMode,
  detectAuthMode,
  getAuthError,
  isTestWebEnv,
  getStoredToken,
  handleAuthCallback,
  isAuthCallback,
  isAuthExchangeInProgress,
  redirectToIESLogin,
  setupEmbeddedAuthListener,
  performLogout,
  clearStoredAuth,
} from '../helpers/AuthStrategy';

interface UserContextProps {
  token: string | undefined;
  user: User | undefined;
  userPrivateGroupInfo: Children | null;
  authMode: AuthMode;
  authError: string | null;
  fetchUser: (fakeUser: boolean, userId?: string) => void;
  updateUserName: (userName: string) => void;
  logout: () => void;
}

const emptyContext: UserContextProps = {
  token: undefined,
  user: undefined,
  userPrivateGroupInfo: null,
  authMode: 'mondly',
  authError: null,
  fetchUser: () => {},
  updateUserName: () => {},
  logout: () => {},
};

const UserContext = createContext(emptyContext);

type UserProviderProps = {
  children: ReactNode;
};

// react memo for preventing re-rendering if variables are unchanged.
export const UserProvider = memo(({ children }: UserProviderProps) => {
  const [token, setToken] = useState<string | undefined>(emptyContext.token);
  const [user, setUser] = useState<User | undefined>(emptyContext.user);
  const [userPrivateGroupInfo, setUserPrivateGroupInfo] = useState<Children | null>(
    emptyContext.userPrivateGroupInfo,
  );
  const [authMode] = useState<AuthMode>(() => detectAuthMode());
  const [authError, setAuthError] = useState<string | null>(getAuthError);
  const { mixpanelTrackEvent, mixpanelTimeEvent } = useMixpanelContext();

  const fetchUser = useCallback(
    async (fakeUser?: boolean, hash?: string) => {
      if (fakeUser) {
        setToken(`fake_token_fake1`);
        setUser({
          id: 'fake1',
          email: 'fake@email.com',
          name: getFromLocalStorage(`fake1-username`) || 'fakeuser',
          state: 1,
          isPrivate: false,
          roles: {
            premium: false,
            liveTutoring: false,
          },
          b2b: {
            companyId: null,
            companyName: null,
            groupId: null,
            groupName: null,
          },
        });
      } else if (hash) {
        const wizResponse = await getHashWizard(hash);
        if (wizResponse) {
          setToken(`wizard_token ${hash}`);
          setUser({
            id: wizResponse.contactId,
            name: wizResponse.username,
            email: wizResponse.email,
            isWizard: true,
            wizardSessionsNum: wizResponse.wizardSessionsNum,
            state: 1,
            isPrivate: false,
            roles: {
              premium: false,
              liveTutoring: false,
            },
            b2b: {
              companyId: null,
              companyName: null,
              groupId: null,
              groupName: null,
            },
          });
          console.log('wizResponse boom', wizResponse);
        }
      } else if (authMode === 'mondly') {
        // Original Mondly flow
        try {
          const resp = await window.mondlyNative?.getUserToken();
          const tokenResponse = resp?.token;
          setToken(tokenResponse);
          if (tokenResponse) {
            const userResponse = await getMondlyUser(tokenResponse);
            if (userResponse) {
              userResponse.id = userResponse.id.toString();
              userResponse.name =
                getFromLocalStorage(`${userResponse.id}-username`) || userResponse.name;
              const isUserPrivate = await isPrivateGroup(userResponse.b2b.groupId);
              userResponse.isPrivate = isUserPrivate;
              setUser(userResponse);
              mixpanelTrackEvent('UserLogin', {
                userDetails: userResponse,
                companyId: userResponse?.b2b.companyId,
                groupId: userResponse?.b2b.groupId,
                groupName: userResponse?.b2b.groupName,
                companyName: userResponse?.b2b.companyName,
                userRoles: userResponse?.roles,
              });
            }
          }
        } catch (err) {
          console.error('Async operation failed:', err);
        }
      } else if (authMode === 'standalone' || authMode === 'embedded') {
        // IES/standalone flow
        try {
          let jwtToken: string | null = null;

          // Check if this is a callback from IES
          if (isAuthCallback()) {
            jwtToken = await handleAuthCallback();
            if (!jwtToken) {
              // Exchange in progress or failed - don't redirect, just wait
              return;
            }
            // Token obtained and stored — reload on root so React Router renders "/"
            window.location.replace('/');
            return;
          }
          // isAuthCallback() returned false — this is a normal page load, not the OAuth callback.
          // Clear any stale exchange-in-progress flag: if the user navigated away while a
          // previous code exchange was still in flight, the flag stays in sessionStorage and
          // would incorrectly suppress the IES login redirect on this load.
          sessionStorage.removeItem('auth_exchange_in_progress');
          // Try to get stored token
          jwtToken = getStoredToken();

          if (jwtToken) {
            setToken(jwtToken);
            // Use the JWT token to get user info from the API
            const userResponse = await getMondlyUser(jwtToken);
            if (userResponse) {
              userResponse.id = userResponse.id.toString();
              userResponse.name =
                getFromLocalStorage(`${userResponse.id}-username`) || userResponse.name;
              const isUserPrivate = await isPrivateGroup(userResponse.b2b?.groupId);
              userResponse.isPrivate = isUserPrivate;
              setUser(userResponse);
              mixpanelTrackEvent('UserLogin', {
                userDetails: userResponse,
                companyId: userResponse?.b2b?.companyId,
                groupId: userResponse?.b2b?.groupId,
                groupName: userResponse?.b2b?.groupName,
                companyName: userResponse?.b2b?.companyName,
                userRoles: userResponse?.roles,
              });
            } else if (authMode === 'standalone') {
              // Token is present but invalid or expired — clear and re-authenticate
              clearStoredAuth();
              redirectToIESLogin();
              setAuthError(getAuthError());
            }
          } else if (authMode === 'standalone' && !isAuthExchangeInProgress()) {
            // No token available and no exchange in progress, redirect to IES login
            redirectToIESLogin();
            setAuthError(getAuthError());
          }
          // For embedded mode without token, we wait for postMessage
        } catch (err) {
          console.error('Standalone/Embedded auth failed:', err);
          if (authMode === 'standalone' && !isAuthExchangeInProgress()) {
            redirectToIESLogin();
            setAuthError(getAuthError());
          }
        }
      }
    },
    [authMode],
  );

  const updateUserName = useCallback((userName: string) => {
    setUser((prevUser) => {
      if (prevUser && prevUser.name !== undefined) {
        return {
          ...prevUser,
          name: userName,
        };
      }
      return prevUser;
    });
  }, []);

  const logout = useCallback(() => {
    if (authMode !== 'mondly') {
      performLogout();
    }
  }, [authMode]);

  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());
  // Use has() for testWeb — get() returns "" (falsy) when param has no value (?testWeb),
  // which would cause the mondly branch to run without a mondlyNative bridge and hang.
  // See isTestWebEnv() in AuthStrategy.ts for the full gating rationale.
  const isFakeUser = isTestWebEnv() && urlSearchParams.has('testWeb');
  useEffect(() => {
    mixpanelTimeEvent('AppFetchUserDuration');
    fetchUser(isFakeUser, params.hash).then(() =>
      mixpanelTrackEvent('AppFetchUserDuration', {
        companyId: user?.b2b.companyId,
        groupId: user?.b2b.groupId,
        groupName: user?.b2b.groupName,
        companyName: user?.b2b.companyName,
        userRoles: user?.roles,
      }),
    );
  }, [fetchUser, mixpanelTimeEvent, mixpanelTrackEvent]);

  // refresh user every 1min (only for Mondly mode):
  useEffect(() => {
    if (authMode === 'mondly') {
      const refreshUserInterval = setInterval(() => {
        if (params) {
          fetchUser(isFakeUser, params.hash);
        }
      }, 60 * 1000);

      return () => clearInterval(refreshUserInterval);
    }
    return undefined;
  }, [authMode]);

  // Setup embedded auth listener (postMessage from parent app)
  useEffect(() => {
    if (authMode === 'embedded') {
      const allowedOrigins = process.env.REACT_APP_ALLOWED_EMBED_ORIGINS?.split(',') || [];
      const cleanup = setupEmbeddedAuthListener(async (receivedToken) => {
        setToken(receivedToken);
        const userResponse = await getMondlyUser(receivedToken);
        if (userResponse) {
          userResponse.id = userResponse.id.toString();
          userResponse.name =
            getFromLocalStorage(`${userResponse.id}-username`) || userResponse.name;
          const isUserPrivate = await isPrivateGroup(userResponse.b2b?.groupId);
          userResponse.isPrivate = isUserPrivate;
          setUser(userResponse);
        }
      }, allowedOrigins);

      return cleanup;
    }
    return undefined;
  }, [authMode]);

  const getPrivateGroupLicenseInfo = async () => {
    if (user && token) {
      if (user.b2b.companyId) {
        const groupLicenseDetails: Children = await getGroupById(user.b2b.companyId, token);
        if (groupLicenseDetails) {
          setUserPrivateGroupInfo(groupLicenseDetails);
        }
      }
    }
  };

  useEffect(() => {
    getPrivateGroupLicenseInfo();
  }, [user]);

  const value = useMemo(
    () => ({
      token,
      user,
      userPrivateGroupInfo,
      authMode,
      authError,
      fetchUser,
      updateUserName,
      logout,
    }),
    [fetchUser, token, updateUserName, user, authMode, authError, logout],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
});

export const useUserContext = () => useContext(UserContext);
